import { clock } from './clock.ts';

/** The shop's wall-clock offset from UTC (minutes) at an instant. */
function wallOffsetMinutes(instantMs: number, timezone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(new Date(instantMs));
  const v = (t: string) => parseInt(parts.find(p => p.type === t)?.value || '0');
  // `% 24`: some ICU builds render midnight as hour "24" under hour12: false.
  const wallMs = Date.UTC(v('year'), v('month') - 1, v('day'), v('hour') % 24, v('minute'), v('second'));
  return Math.round((wallMs - Math.floor(instantMs / 1000) * 1000) / 60000);
}

/**
 * Converts minutes from midnight in a shop's timezone to a UTC Date object
 * Robust algorithm that ensures the wall-clock time in `timezone` equals
 * the provided minutes at `dateStr`.
 *
 * Core-100 R4 wave 2 (CORE-AC Rel, DST): the offset is taken at the RESULT instant, not at
 * the "wall clock read as UTC" guess. The old single pass sampled the offset hours away from
 * the real instant, so on a DST-change day every wall time between the change and the UTC
 * offset hour came out one hour off (e.g. America/Los_Angeles 2026-03-08 07:30 -> 15:30Z
 * instead of 14:30Z): a multi-day chain segment starting that morning was planned an hour
 * late. A wall time that does not exist (the spring-forward gap) moves forward by the gap; an
 * ambiguous one (fall-back) is its first occurrence.
 */
export function makeUTCFromShopMinutes(
  dateStr: string,
  minutes: number,
  timezone: string
): Date {
  const [y, m, d] = dateStr.split('-').map(Number);
  const hh = Math.floor(minutes / 60);
  const mm = minutes % 60;

  // The wall-clock time read as if it were UTC.
  const wallMs = Date.UTC(y, m - 1, d, hh, mm, 0);
  const off1 = wallOffsetMinutes(wallMs, timezone);
  let instant = wallMs - off1 * 60000;
  const off2 = wallOffsetMinutes(instant, timezone);
  if (off2 !== off1) {
    const retry = wallMs - off2 * 60000;
    // Keep the retry only when it reads back with its own offset (it does, except in the gap).
    if (wallOffsetMinutes(retry, timezone) === off2) instant = retry;
  }
  return new Date(instant);
}

/**
 * A same-day work window planned in shop-local minutes, as instants: the start's true
 * instant, and the finish as the start plus the window's elapsed wall minutes — the
 * `windowFinishInstant` rule of `finishIntentDerivation.ts` (Core-100 R4-22). A window that
 * crosses a DST change keeps its real length.
 */
export function makeShopWindowInstants(
  dateStr: string,
  startMin: number,
  finishMin: number,
  timezone: string
): { start: Date; finish: Date } {
  const start = makeUTCFromShopMinutes(dateStr, startMin, timezone);
  return { start, finish: new Date(start.getTime() + (finishMin - startMin) * 60000) };
}

/**
 * Converts a UTC ISO string to minutes from midnight in a shop's timezone
 */
export function getMinutesInShopTZ(utcISO: string, timezone: string): number {
  const date = new Date(utcISO);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  }).formatToParts(date);

  // `% 24`: under `hour12: false`, some ICU builds (e.g. Node 20's) render
  // midnight as hour "24" instead of "00", which would make 00:30 compute as
  // 1470 minutes instead of 30. Normalizing 24→0 is a no-op for valid 0–23.
  const h = parseInt(parts.find(p => p.type === 'hour')?.value || '0') % 24;
  const m = parseInt(parts.find(p => p.type === 'minute')?.value || '0');
  return h * 60 + m;
}

/**
 * Gets the current date (YYYY-MM-DD) in the shop's timezone
 * CRITICAL: Use this instead of `new Date().toISOString().split('T')[0]` to avoid UTC drift
 *
 * Chronomancer R-PAST-10: routes through `clock.now()` so edge-function
 * tests that set `FIXED_TIME` env var see a stable "today" value.
 * Previously used raw `new Date()`, so reassignCore's past-time guards
 * (lines 992 and 1077) computed real wall-clock today under FIXED_TIME
 * tests, while downstream `nowMin` computations routed through
 * `clock.now()` — produced inconsistent "isTargetDateFuture" results
 * in test mode. Matches the R-PAST-4 / R-PAST-5 pattern.
 */
export function getTodayInShopTimezone(timezone: string): string {
  const now = clock.now();
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(now);

  const year = parts.find(p => p.type === 'year')?.value || '2026';
  const month = parts.find(p => p.type === 'month')?.value || '01';
  const day = parts.find(p => p.type === 'day')?.value || '01';
  return `${year}-${month}-${day}`;
}
